import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <Link to="/" className="back-link">&larr; Back</Link>
      <h1>Privacy Policy</h1>
      <p className="legal-date">Effective Date: March 30, 2026</p>

      <p>
        MileDeduct ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
        explains how we collect, use, disclose, and safeguard your information when you use the MileDeduct
        mobile application and website (collectively, the "Service"). Please read this Privacy Policy
        carefully. By using the Service, you consent to the practices described herein.
      </p>

      <h2>1. Information We Collect</h2>

      <p><strong>1.1 Information You Provide</strong></p>
      <ul>
        <li><strong>Account Information:</strong> Name, email address, and authentication credentials when you create an account or sign in via Google.</li>
        <li><strong>Vehicle Information:</strong> Make, model, year, odometer readings, and vehicle nicknames.</li>
        <li><strong>Trip Data:</strong> Start and end addresses, routes, distances, trip purposes, dates, and any notes you add to trips.</li>
        <li><strong>Payment Information:</strong> Billing details processed through Stripe. We do not store your full credit card number on our servers.</li>
      </ul>

      <p><strong>1.2 Information Collected Automatically</strong></p>
      <ul>
        <li><strong>Location Data:</strong> With your permission, we collect GPS coordinates to record your routes and calculate mileage. This includes (a) continuous background location data when "Always Allow" location access is granted, used to automatically detect when you begin driving and record trips, and (b) GPS coordinates during manually started trip tracking sessions. Background location data is processed on-device using motion sensors to distinguish driving from other activities. Only confirmed trip coordinates are stored on our servers.</li>
        <li><strong>Motion & Fitness Data:</strong> With your permission, we access your device's motion sensors (accelerometer, gyroscope) to detect automotive activity and automatically start trip recording when driving is detected. This data is processed entirely on-device and is not transmitted to our servers.</li>
        <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, and app version.</li>
        <li><strong>Usage Data:</strong> Features used, screens viewed, session duration, and interaction patterns to improve the Service.</li>
      </ul>

      <p><strong>1.3 Information from Third Parties</strong></p>
      <ul>
        <li><strong>Google Sign-In:</strong> If you sign in with Google, we receive your name, email address, and profile photo from Google.</li>
        <li><strong>Google Maps Platform:</strong> We use Google Maps APIs to display maps, calculate routes, and perform geocoding (converting coordinates to addresses).</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service.</li>
        <li>Track and record your mileage for business trip documentation.</li>
        <li>Calculate estimated IRS mileage deductions.</li>
        <li>Generate trip history reports and summaries.</li>
        <li>Process subscription payments.</li>
        <li>Communicate with you about your account, updates, and support requests.</li>
        <li>Detect, prevent, and address technical issues, fraud, or abuse.</li>
        <li>Comply with legal obligations.</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
      <ul>
        <li><strong>Service Providers:</strong> We share data with trusted third-party providers who assist us in operating the Service, including Firebase (Google Cloud) for authentication and data storage, Stripe for payment processing, and Google Maps Platform for mapping and geocoding.</li>
        <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request.</li>
        <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.</li>
        <li><strong>With Your Consent:</strong> We may share your information for other purposes with your explicit consent.</li>
      </ul>

      <h2>4. Location Data</h2>
      <p>
        Location data is central to the Service's functionality. We collect precise GPS coordinates in
        two ways:
      </p>
      <ul>
        <li><strong>Automatic Trip Detection:</strong> When you grant "Always Allow" location access,
        the app uses background location monitoring combined with on-device motion analysis to detect
        when you are driving. When a trip is detected, the app records GPS coordinates along your route.
        Background location monitoring uses low-power significant location changes when not actively
        tracking a trip, and high-accuracy GPS only during confirmed driving activity.</li>
        <li><strong>Manual GPS Tracking:</strong> When you manually start a GPS trip, the app records
        your route coordinates for the duration of that session.</li>
      </ul>
      <p>
        You can revoke location permissions at any time through your device's Settings app (iOS: Settings
        &gt; MileDeduct &gt; Location). Revoking "Always Allow" will disable automatic trip detection but
        still allow manual GPS tracking with "While Using" permission. Revoking all location access will
        disable GPS tracking entirely; you can still log trips manually.
      </p>
      <p>
        <strong>Important:</strong> Motion and fitness data used for drive detection is processed entirely
        on your device. We do not collect, store, or transmit raw motion sensor data to our servers.
      </p>

      <h2>5. Data Storage and Security</h2>
      <p>
        Your data is stored using Google Firebase (Cloud Firestore) with data centers located in the
        United States. We implement industry-standard security measures including:
      </p>
      <ul>
        <li>Encryption of data in transit (TLS/SSL) and at rest.</li>
        <li>Firebase Authentication for secure account access.</li>
        <li>Firestore Security Rules to restrict data access to authorized users.</li>
        <li>Regular security reviews and updates.</li>
      </ul>
      <p>
        While we strive to protect your information, no method of electronic storage or transmission is
        100% secure. We cannot guarantee absolute security.
      </p>

      <h2>6. Data Retention</h2>
      <p>
        We retain your personal information and trip data for as long as your account is active or as needed
        to provide the Service. If you delete your account, we will delete or anonymize your personal data
        within 30 days, except where retention is required by law (e.g., financial records for tax or
        accounting purposes).
      </p>

      <h2>7. Your Rights and Choices</h2>
      <p>Depending on your jurisdiction, you may have the following rights:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal information.</li>
        <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements.</li>
        <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format.</li>
        <li><strong>Opt-Out:</strong> Opt out of promotional communications at any time.</li>
        <li><strong>Location Permissions:</strong> Revoke location access through your device settings.</li>
      </ul>
      <p>To exercise these rights, contact us at <strong>privacy@milededuct.app</strong>.</p>

      <h2>8. Children's Privacy</h2>
      <p>
        The Service is not directed to individuals under the age of 18. We do not knowingly collect personal
        information from children. If we become aware that we have collected personal information from a
        child under 18, we will take steps to delete such information promptly.
      </p>

      <h2>9. California Privacy Rights (CCPA)</h2>
      <p>
        If you are a California resident, you have additional rights under the California Consumer Privacy
        Act (CCPA), including the right to know what personal information we collect, the right to request
        deletion, and the right to opt out of the sale of personal information. We do not sell personal
        information. To exercise your CCPA rights, contact us at <strong>privacy@milededuct.app</strong>.
      </p>

      <h2>10. International Users</h2>
      <p>
        The Service is operated from the United States. If you are accessing the Service from outside the
        United States, please be aware that your information will be transferred to, stored, and processed
        in the United States. By using the Service, you consent to this transfer.
      </p>

      <h2>11. Third-Party Links and Services</h2>
      <p>
        The Service may contain links to third-party websites or integrate with third-party services.
        We are not responsible for the privacy practices of these third parties. We encourage you to review
        their privacy policies.
      </p>

      <h2>12. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes by
        posting the updated policy on the Service and updating the "Effective Date" above. Your continued
        use of the Service after changes are posted constitutes your acceptance of the revised policy.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have any questions or concerns about this Privacy Policy or our data practices, please
        contact us at:
      </p>
      <ul>
        <li><strong>Email:</strong> privacy@milededuct.app</li>
        <li><strong>Website:</strong> milededuct.app</li>
      </ul>

      <p style={{ marginTop: '32px', fontSize: '13px', color: '#64748B' }}>
        By using MileDeduct, you acknowledge that you have read and understood this Privacy Policy and
        agree to the collection and use of your information as described herein.
      </p>
    </div>
  );
}
